"use client";

import Header from "@/app/components/ui/header";
import Container from "@/app/components/ui/container";
import { LinkAnchor } from "@/app/components/ui/link-anchor";
import React from "react";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "@/app/components/ui/collapsible";
import { ListTree, ChevronDown, ChevronUp } from "lucide-react";
import { getTenantConfigByHost } from "@/app/config/tenant-runtime";

// Strict type for TOC items
export type TDatenschutzTocItem = {
  id: string;
  title: string;
  children?: { id: string; title: string }[];
};

type HeadingProps = {
  id: string;
  children: React.ReactNode;
};

const DATENSCHUTZ_TOC_BASE: TDatenschutzTocItem[] = [
  {
    id: "ueberblick",
    title: "1. Datenschutz auf einen Blick",
    children: [{ id: "allgemeine-hinweise", title: "Allgemeine Hinweise" }],
  },
  {
    id: "datenerfassung",
    title: "2. Datenerfassung auf dieser Website",
    children: [
      { id: "verantwortliche-stelle", title: "Wer ist verantwortlich?" },
      { id: "wie-erfassen", title: "Wie erfassen wir Ihre Daten?" },
      { id: "nutzung-daten", title: "Wofür nutzen wir Ihre Daten?" },
      { id: "rechte", title: "Welche Rechte haben Sie?" },
      {
        id: "analyse-tools",
        title: "Analyse-Tools und Tools von Drittanbietern",
      },
    ],
  },
  {
    id: "hosting-cdn",
    title: "3. Hosting und Content Delivery Networks (CDN)",
    children: [
      { id: "vercel", title: "Vercel" },
      { id: "cloudflare", title: "Cloudflare" },
      { id: "supabase", title: "Supabase" },
      { id: "make-com", title: "Make.com" },
    ],
  },
  {
    id: "pflichtinfos",
    title: "4. Allgemeine Hinweise und Pflichtinformationen",
    children: [
      { id: "datenschutz", title: "Datenschutz" },
      {
        id: "verantwortliche-stelle-2",
        title: "Hinweis zur verantwortlichen Stelle",
      },
      { id: "speicherdauer", title: "Speicherdauer" },
      {
        id: "rechtsgrundlagen",
        title: "Rechtsgrundlagen der Datenverarbeitung",
      },
      { id: "datenschutzbeauftragter", title: "Datenschutzbeauftragter" },
      {
        id: "datenweitergabe",
        title: "Datenweitergabe in die USA und Drittstaaten",
      },
      { id: "widerruf", title: "Widerruf Ihrer Einwilligung" },
      { id: "widerspruch", title: "Widerspruchsrecht & Direktwerbung" },
      {
        id: "aufsichtsbehoerde",
        title: "Beschwerderecht bei der Aufsichtsbehörde",
      },
      { id: "datenuebertragbarkeit", title: "Recht auf Datenübertragbarkeit" },
      {
        id: "auskunft-loeschung",
        title: "Auskunft, Löschung und Berichtigung",
      },
      {
        id: "einschraenkung",
        title: "Recht auf Einschränkung der Verarbeitung",
      },
      { id: "ssl-tls", title: "SSL- bzw. TLS-Verschlüsselung" },
      { id: "werbe-emails", title: "Widerspruch gegen Werbe-E-Mails" },
    ],
  },
  {
    id: "datenerfassung-website",
    title:
      "5. Datenerfassung auf dieser Website (Server-Log, Kontakt, Cookies)",
    children: [
      { id: "server-log", title: "Server-Log-Dateien" },
      { id: "kontaktformular", title: "Kontaktformular" },
      { id: "anfrage", title: "Anfrage per E-Mail, Telefon oder Telefax" },
      { id: "cookies", title: "Cookies und Einwilligungs-Management" },
      { id: "google-tag-manager", title: "Google Tag Manager" },
      { id: "google-analytics", title: "Google Analytics" },
      { id: "meta-pixel", title: "Meta Pixel (Facebook-Pixel)" },
    ],
  },
  {
    id: "newsletter",
    title: "6. Newsletter",
    children: [{ id: "newsletterdaten", title: "Newsletter­daten" }],
  },
  {
    id: "einbindung-externe",
    title: "7. Einbindung von externen Inhalten",
    children: [
      { id: "youtube", title: "Einbindung von YouTube" },
      { id: "facebook-instagram", title: "Facebook- und Instagram-Inhalte" },
    ],
  },
  {
    id: "social-media-profile",
    title: "8. Social-Media-Profile der WIEST GROUP",
    children: [],
  },
];

const createDatenschutzToc = (
  isSchuetzTenant: boolean
): TDatenschutzTocItem[] =>
  DATENSCHUTZ_TOC_BASE.map((item) => {
    if (item.id !== "datenerfassung-website" || !isSchuetzTenant) {
      return item;
    }

    return {
      ...item,
      children: [
        item.children?.[0],
        item.children?.[1],
        item.children?.[2],
        {
          id: "ki-sprachassistent",
          title: "KI-gestützter Sprachassistent",
        },
        ...(item.children?.slice(3) ?? []),
      ].filter(Boolean) as NonNullable<typeof item.children>,
    };
  });

function DatenschutzToc({
  items,
  onSectionClick,
}: {
  items: TDatenschutzTocItem[];
  onSectionClick: (id: string) => void;
}) {
  const [open, setOpen] = React.useState(false); // default: closed
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="mb-6 lg:mb-10">
      <CollapsibleTrigger className="flex cursor-pointer items-center gap-2 transition select-none">
        <ListTree className="text-primary size-5" />
        <span className="text-primary text-lg font-semibold">
          Inhaltsverzeichnis
        </span>
        <span className="ml-auto flex items-center">
          {open ? (
            <ChevronUp
              className="text-muted-foreground size-5"
              aria-label="Inhaltsverzeichnis schließen"
            />
          ) : (
            <ChevronDown
              className="text-muted-foreground size-5"
              aria-label="Inhaltsverzeichnis öffnen"
            />
          )}
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent className="px-0 pt-4">
        <ul className="space-y-2">
          {items.map((item) => (
            <li key={item.id}>
              <LinkAnchor
                href={`#${item.id}`}
                variant="primary"
                underline="always"
                size="md"
                className="font-semibold"
                onClick={() => onSectionClick(item.id)}
              >
                {item.title}
              </LinkAnchor>
              {item.children && (
                <ul className="border-muted-foreground/20 mt-1 ml-4 space-y-1 border-l pl-3">
                  {item.children.map((child) => (
                    <li key={child.id}>
                      <LinkAnchor
                        href={`#${child.id}`}
                        variant="subtle"
                        underline="always"
                        size="sm"
                        onClick={() => onSectionClick(child.id)}
                      >
                        {child.title}
                      </LinkAnchor>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  );
}

export default function Page() {
  // State for currently highlighted heading
  const [activeHeadingId, setActiveHeadingId] = React.useState<string | null>(
    null
  );
  const [tenantKey, setTenantKey] = React.useState<string | null>(null);
  const highlightTimeout = React.useRef<NodeJS.Timeout | null>(null);
  const isSchuetzTenant = tenantKey === "schuetz-gross-umstadt";

  React.useEffect(() => {
    setTenantKey(getTenantConfigByHost(window.location.hostname).key);
  }, []);

  const datenschutzToc = createDatenschutzToc(isSchuetzTenant);

  // Hilfsfunktion für Heading-Highlight
  const getHeadingHighlight = (id: string) =>
    id === activeHeadingId
      ? "bg-yellow-200 rounded px-2 -mx-2 transition-colors duration-300"
      : "";

  const sectionSpacing = "scroll-mt-28 space-y-8";
  const blockSpacing = "space-y-4";
  const denseBlockSpacing = "space-y-3";

  const SectionHeading = ({ id, children }: HeadingProps) => (
    <h2 id={id} className="heading-large text-gray-900">
      <span className={getHeadingHighlight(id)}>{children}</span>
    </h2>
  );

  const SubHeading = ({ id, children }: HeadingProps) => (
    <h3 id={id} className="heading-medium scroll-mt-24 text-gray-900">
      <span className={getHeadingHighlight(id)}>{children}</span>
    </h3>
  );

  const SubSubHeading = ({ id, children }: HeadingProps) => (
    <h4 id={id} className="heading-small scroll-mt-20 text-gray-900">
      <span className={getHeadingHighlight(id)}>{children}</span>
    </h4>
  );

  // Handler for TOC link click
  const handleTocClick = (id: string) => {
    setActiveHeadingId(null);
    setTimeout(() => {
      setActiveHeadingId(id);
      if (highlightTimeout.current) clearTimeout(highlightTimeout.current);
      highlightTimeout.current = setTimeout(
        () => setActiveHeadingId(null),
        4000
      );
    }, 10);
  };

  // Highlight auch bei direktem Aufruf per URL-Hash oder Browser-Navigation
  React.useEffect(() => {
    const highlightFromHash = () => {
      const hash = window.location.hash.replace("#", "");
      if (hash) {
        setActiveHeadingId(null);
        setTimeout(() => {
          setActiveHeadingId(hash);
          if (highlightTimeout.current) clearTimeout(highlightTimeout.current);
          highlightTimeout.current = setTimeout(
            () => setActiveHeadingId(null),
            4000
          );
        }, 10);
      }
    };
    // Initial bei Mount
    highlightFromHash();
    // Bei Hash-Änderung (z. B. durch Browser-Navigation)
    window.addEventListener("hashchange", highlightFromHash);
    return () => {
      window.removeEventListener("hashchange", highlightFromHash);
      if (highlightTimeout.current) clearTimeout(highlightTimeout.current);
    };
  }, []);

  return (
    <>
      <Header
        title="Datenschutzerklärung"
        image="https://zdpbibnixeydfncoraga.supabase.co/storage/v1/object/public/header-images//header_image-grosskunden_verkaufsraum.webp"
      />
      <Container>
        <main className="py-12 text-gray-900 lg:py-16">
          <div className={`space-y-14 leading-relaxed`}>
            <DatenschutzToc
              items={datenschutzToc}
              onSectionClick={handleTocClick}
            />

            {/* 1. Datenschutz auf einen Blick */}
            <section id="ueberblick" className={sectionSpacing}>
              <SectionHeading id="ueberblick">
                1. Datenschutz auf einen Blick
              </SectionHeading>
              <div className={blockSpacing}>
                <SubHeading id="allgemeine-hinweise">
                  Allgemeine Hinweise
                </SubHeading>
                <p>
                  Die folgenden Hinweise geben einen einfachen Überblick
                  darüber, was mit Ihren personenbezogenen Daten passiert, wenn
                  Sie diese Website besuchen. Personenbezogene Daten sind alle
                  Daten, mit denen Sie persönlich identifiziert werden können.
                  Ausführliche Informationen zum Thema Datenschutz entnehmen Sie
                  unserer unter diesem Text aufgeführten Datenschutzerklärung.
                </p>
              </div>
            </section>

            {/* 2. Datenerfassung auf dieser Website */}
            <section id="datenerfassung" className={sectionSpacing}>
              <SectionHeading id="datenerfassung">
                2. Datenerfassung auf dieser Website
              </SectionHeading>
              <div className="space-y-6">
                <div className={blockSpacing}>
                  <SubHeading id="verantwortliche-stelle">
                    Wer ist verantwortlich für die Datenerfassung auf dieser
                    Website?
                  </SubHeading>
                  <p>
                    Die Datenverarbeitung auf dieser Website erfolgt durch den
                    Websitebetreiber. Dessen Kontaktdaten können Sie dem
                    Abschnitt „Hinweis zur Verantwortlichen Stelle” in dieser
                    Datenschutzerklärung entnehmen.
                  </p>
                </div>
                <div className={blockSpacing}>
                  <SubHeading id="wie-erfassen">
                    Wie erfassen wir Ihre Daten?
                  </SubHeading>
                  <p>
                    Ihre Daten werden zum einen dadurch erhoben, dass Sie uns
                    diese mitteilen. Hierbei kann es sich z. B. um Daten
                    handeln, die Sie in ein Kontaktformular eingeben.
                  </p>
                  <p>
                    Andere Daten werden automatisch oder nach Ihrer Einwilligung
                    beim Besuch der Website durch unsere IT-Systeme erfasst. Das
                    sind vor allem technische Daten (z. B. Internetbrowser,
                    Betriebssystem oder Uhrzeit des Seitenaufrufs). Die
                    Erfassung dieser Daten erfolgt automatisch, sobald Sie diese
                    Website betreten.
                  </p>
                </div>
                <div className={blockSpacing}>
                  <SubHeading id="nutzung-daten">
                    Wofür nutzen wir Ihre Daten?
                  </SubHeading>
                  <p>
                    Ein Teil der Daten wird erhoben, um eine fehlerfreie
                    Bereitstellung der Website zu gewährleisten. Andere Daten
                    können zur Analyse Ihres Nutzerverhaltens verwendet werden.
                  </p>
                </div>
                <div className={blockSpacing}>
                  <SubHeading id="rechte">
                    Welche Rechte haben Sie bezüglich Ihrer Daten?
                  </SubHeading>
                  <p>
                    Sie haben jederzeit das Recht, unentgeltlich Auskunft über
                    Herkunft, Empfänger und Zweck Ihrer gespeicherten
                    personenbezogenen Daten zu erhalten. Sie haben außerdem ein
                    Recht, die Berichtigung oder Löschung dieser Daten zu
                    verlangen. Wenn Sie eine Einwilligung zur Datenverarbeitung
                    erteilt haben, können Sie diese Einwilligung jederzeit für
                    die Zukunft widerrufen. Außerdem haben Sie das Recht, unter
                    bestimmten Umständen die Einschränkung der Verarbeitung
                    Ihrer personenbezogenen Daten zu verlangen. Des Weiteren
                    steht Ihnen ein Beschwerderecht bei der zuständigen
                    Aufsichtsbehörde zu.
                  </p>
                  <p>
                    Hierzu sowie zu weiteren Fragen zum Thema Datenschutz können
                    Sie sich jederzeit an uns wenden.
                  </p>
                </div>
                <div className={blockSpacing}>
                  <SubHeading id="analyse-tools">
                    Analyse-Tools und Tools von Drittanbietern
                  </SubHeading>
                  <p>
                    Beim Besuch dieser Website kann Ihr Surf-Verhalten
                    statistisch ausgewertet werden. Das geschieht vor allem mit
                    sogenannten Analyseprogrammen. Detaillierte Informationen zu
                    diesen Analyseprogrammen finden Sie in der folgenden
                    Datenschutzerklärung.
                  </p>
                </div>
              </div>
            </section>

            {/* 3. Hosting und Content Delivery Networks (CDN) */}
            <section id="hosting-cdn" className={sectionSpacing}>
              <SectionHeading id="hosting-cdn">
                3. Hosting und Content Delivery Networks (CDN)
              </SectionHeading>
              <div className="space-y-8">
                <div className={blockSpacing}>
                  <SubHeading id="vercel">Vercel</SubHeading>
                  <div className={denseBlockSpacing}>
                    <p>
                      Wir hosten die Inhalte unserer Website bei einem externen
                      Dienstleister.
                    </p>
                    <SubSubHeading id="vercel-hoster">Hoster</SubSubHeading>
                    <p className="font-medium">Vercel Inc.</p>
                    <p>440 N Barranca Ave #4133</p>
                    <p>Covina, CA 91723</p>
                    <p>USA</p>
                    <p>(nachfolgend &ldquo;Hoster&rdquo;)</p>
                    <p>
                      Wenn Sie unsere Website besuchen, erfasst der Hoster
                      verschiedene Logfiles inklusive Ihrer IP-Adresse. Der
                      Einsatz des Hosters erfolgt zum Zwecke der sicheren,
                      schnellen und zuverlässigen Bereitstellung unseres
                      Online-Angebots.
                    </p>
                    <p>
                      Die Verwendung des Hosters erfolgt auf Grundlage von Art.
                      6 Abs. 1 lit. f DSGVO. Wir haben ein berechtigtes
                      Interesse an einer möglichst zuverlässigen und
                      performanten Darstellung unserer Website. Sofern eine
                      entsprechende Einwilligung abgefragt wurde, erfolgt die
                      Verarbeitung ausschließlich auf Grundlage von Art. 6 Abs.
                      1 lit. a DSGVO und § 25 Abs. 1 TTDSG, soweit die
                      Einwilligung die Speicherung von Cookies oder den Zugriff
                      auf Informationen im Endgerät des Nutzers (z. B.
                      Device-Fingerprinting) im Sinne des TTDSG umfasst. Die
                      Einwilligung ist jederzeit widerrufbar.
                    </p>
                    <p>
                      Mit dem Hoster haben wir einen Vertrag über
                      Auftragsverarbeitung (AVV) geschlossen. Dieser stellt
                      sicher, dass der Hoster personenbezogene Daten der
                      Websitebesucher nur nach unseren Weisungen und unter
                      Einhaltung der DSGVO verarbeitet.
                    </p>
                    <p>
                      Details entnehmen Sie der{" "}
                      <LinkAnchor
                        href="https://vercel.com/legal/privacy-policy"
                        variant="primary"
                        underline="always"
                        target="_blank"
                        rel="nofollow"
                      >
                        Datenschutzerklärung von Vercel
                      </LinkAnchor>
                      .
                    </p>
                  </div>
                </div>
                <div className={blockSpacing}>
                  <SubHeading id="cloudflare">Cloudflare</SubHeading>
                  <div className={denseBlockSpacing}>
                    <p>
                      Wir nutzen den Service „Cloudflare”. Anbieter ist die
                      Cloudflare Inc., 101 Townsend St., San Francisco, CA
                      94107, USA (im Folgenden „Cloudflare”).
                    </p>
                    <p>
                      Cloudflare bietet ein weltweit verteiltes Content Delivery
                      Network mit DNS an. Dabei wird technisch der
                      Informationstransfer zwischen Ihrem Browser und unserer
                      Website über das Netzwerk von Cloudflare geleitet. Das
                      versetzt Cloudflare in die Lage, den Datenverkehr zwischen
                      Ihrem Browser und unserer Website zu analysieren und als
                      Filter zwischen unseren Servern und potenziell bösartigem
                      Datenverkehr aus dem Internet zu dienen. Hierbei kann
                      Cloudflare auch Cookies oder sonstige Technologien zur
                      Wiedererkennung von Internetnutzern einsetzen, die jedoch
                      allein zum hier beschriebenen Zweck verwendet werden.
                    </p>
                    <p>
                      Der Einsatz von Cloudflare beruht auf unserem berechtigten
                      Interesse an einer möglichst fehlerfreien und sicheren
                      Bereitstellung unseres Webangebotes (Art. 6 Abs. 1 lit. f
                      DSGVO).
                    </p>
                    <p>
                      Die Datenübertragung in die USA wird auf die
                      Standardvertragsklauseln der EU-Kommission gestützt.{" "}
                      <LinkAnchor
                        href="https://www.cloudflare.com/privacypolicy/"
                        variant="primary"
                        underline="always"
                        target="_blank"
                        rel="nofollow"
                      >
                        Details finden Sie in der Datenschutzerklärung von
                        Cloudflare
                      </LinkAnchor>
                      .
                    </p>
                    <p>
                      <LinkAnchor
                        href="https://www.cloudflare.com/privacypolicy/"
                        variant="primary"
                        underline="always"
                        target="_blank"
                        rel="nofollow"
                      >
                        Weitere Informationen zum Thema Sicherheit und
                        Datenschutz bei Cloudflare
                      </LinkAnchor>
                      .
                    </p>
                    <SubSubHeading id="cloudflare-avv">
                      Auftragsverarbeitung
                    </SubSubHeading>
                    <p>
                      Wir haben einen Vertrag über Auftragsverarbeitung (AVV)
                      zur Nutzung des oben genannten Dienstes geschlossen.
                      Hierbei handelt es sich um einen datenschutzrechtlich
                      vorgeschriebenen Vertrag, der gewährleistet, dass dieser
                      die personenbezogenen Daten unserer Websitebesucher nur
                      nach unseren Weisungen und unter Einhaltung der DSGVO
                      verarbeitet.
                    </p>
                  </div>
                </div>
                <div className={blockSpacing}>
                  <SubHeading id="supabase">Supabase</SubHeading>
                  <div className={denseBlockSpacing}>
                    <p>
                      Wir nutzen die Dienste von Supabase für die Datenbank- und
                      Backend-Funktionalitäten unserer Website. Anbieter ist die
                      Supabase Inc., 970 Toa Payoh North #07-04, Singapore
                      318992, Singapur („Google“),
                    </p>
                    <p>
                      Supabase bietet eine cloudbasierte Datenbank- und
                      Backend-Plattform an. Dabei werden personenbezogene Daten,
                      die Sie über unsere Website eingeben (z. B.
                      Kontaktformulare, Registrierungen), auf Servern von
                      Supabase gespeichert und verarbeitet. Supabase kann dabei
                      verschiedene technische Daten wie IP-Adressen,
                      Zugriffszeiten und Metadaten erfassen.
                    </p>
                    <p>
                      Der Einsatz von Supabase erfolgt auf Grundlage von Art. 6
                      Abs. 1 lit. f DSGVO. Wir haben ein berechtigtes Interesse
                      an einer zuverlässigen und sicheren Speicherung und
                      Verarbeitung der Daten. Sofern eine entsprechende
                      Einwilligung abgefragt wurde, erfolgt die Verarbeitung
                      ausschließlich auf Grundlage von Art. 6 Abs. 1 lit. a
                      DSGVO und § 25 Abs. 1 TTDSG, soweit die Einwilligung die
                      Speicherung von Cookies oder den Zugriff auf Informationen
                      im Endgerät des Nutzers (z. B. Device-Fingerprinting) im
                      Sinne des TTDSG umfasst. Die Einwilligung ist jederzeit
                      widerrufbar.
                    </p>
                    <p className="mt-2">
                      Die Datenübertragung in Drittstaaten (Singapur, USA) wird
                      auf die Standardvertragsklauseln der EU-Kommission
                      gestützt.{" "}
                      <LinkAnchor
                        href="https://supabase.com/privacy"
                        variant="primary"
                        underline="always"
                        target="_blank"
                        rel="nofollow"
                      >
                        Details finden Sie in der Datenschutzerklärung von
                        Supabase
                      </LinkAnchor>
                      .
                    </p>
                    <SubSubHeading id="supabase-avv">
                      Auftragsverarbeitung
                    </SubSubHeading>
                    <p>
                      Wir haben einen Vertrag über Auftragsverarbeitung (AVV)
                      zur Nutzung des oben genannten Dienstes geschlossen.
                      Hierbei handelt es sich um einen datenschutzrechtlich
                      vorgeschriebenen Vertrag, der gewährleistet, dass dieser
                      die personenbezogenen Daten unserer Websitebesucher nur
                      nach unseren Weisungen und unter Einhaltung der DSGVO
                      verarbeitet.
                    </p>
                  </div>
                </div>
                <div className={blockSpacing}>
                  <SubHeading id="make-com">Make.com</SubHeading>
                  <div className={denseBlockSpacing}>
                    <p>
                      Wir nutzen die Dienste von Make.com (ehemals Integromat)
                      für die Automatisierung von Geschäftsprozessen und die
                      Integration verschiedener Dienste. Anbieter ist die
                      Make.com s.r.o., V Parku 2316/16, 148 00 Praha 4,
                      Tschechische Republik.
                    </p>
                    <p>
                      Make.com ermöglicht es uns, verschiedene Online-Dienste
                      und Systeme zu verbinden und Daten zwischen diesen zu
                      übertragen und zu verarbeiten. Dabei können
                      personenbezogene Daten, die Sie über unsere Website
                      eingeben oder die im Rahmen der Nutzung unserer Dienste
                      anfallen, über Make.com verarbeitet werden. Dies umfasst
                      insbesondere Daten aus Kontaktformularen, Registrierungen
                      oder anderen Interaktionen mit unserer Website.
                    </p>
                    <p>
                      Der Einsatz von Make.com erfolgt auf Grundlage von Art. 6
                      Abs. 1 lit. f DSGVO. Wir haben ein berechtigtes Interesse
                      an einer effizienten Automatisierung von
                      Geschäftsprozessen und der Integration verschiedener
                      Systeme. Sofern eine entsprechende Einwilligung abgefragt
                      wurde, erfolgt die Verarbeitung ausschließlich auf
                      Grundlage von Art. 6 Abs. 1 lit. a DSGVO und § 25 Abs. 1
                      TTDSG, soweit die Einwilligung die Speicherung von Cookies
                      oder den Zugriff auf Informationen im Endgerät des Nutzers
                      (z. B. Device-Fingerprinting) im Sinne des TTDSG umfasst.
                      Die Einwilligung ist jederzeit widerrufbar.
                    </p>

                    <p className="mt-2">
                      Die Datenübertragung in Drittstaaten kann nicht
                      ausgeschlossen werden. Make.com stützt sich hierbei auf
                      die Standardvertragsklauseln der EU-Kommission.{" "}
                      <LinkAnchor
                        href="https://www.make.com/en/privacy"
                        variant="primary"
                        underline="always"
                        target="_blank"
                        rel="nofollow"
                      >
                        Details finden Sie in der Datenschutzerklärung von
                        Make.com
                      </LinkAnchor>
                      .
                    </p>
                    <SubSubHeading id="make-com-avv">
                      Auftragsverarbeitung
                    </SubSubHeading>
                    <p>
                      Wir haben einen Vertrag über Auftragsverarbeitung (AVV)
                      zur Nutzung des oben genannten Dienstes geschlossen.
                      Hierbei handelt es sich um einen datenschutzrechtlich
                      vorgeschriebenen Vertrag, der gewährleistet, dass dieser
                      die personenbezogenen Daten unserer Websitebesucher nur
                      nach unseren Weisungen und unter Einhaltung der DSGVO
                      verarbeitet.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* 4. Allgemeine Hinweise und Pflichtinformationen */}
            <section id="pflichtinfos" className={sectionSpacing}>
              <SectionHeading id="pflichtinfos">
                4. Allgemeine Hinweise und Pflichtinformationen
              </SectionHeading>
              <div className="space-y-6">
                <div className={blockSpacing}>
                  <SubHeading id="datenschutz">Datenschutz</SubHeading>
                  <p>
                    Die Betreiber dieser Seiten nehmen den Schutz Ihrer
                    persönlichen Daten sehr ernst. Wir behandeln Ihre
                    personenbezogenen Daten vertraulich und entsprechend den
                    gesetzlichen Datenschutzvorschriften sowie dieser
                    Datenschutzerklärung.
                  </p>
                  <p>
                    Wenn Sie diese Website benutzen, werden verschiedene
                    personenbezogene Daten erhoben. Personenbezogene Daten sind
                    Daten, mit denen Sie persönlich identifiziert werden können.
                    Die vorliegende Datenschutzerklärung erläutert, welche Daten
                    wir erheben und wofür wir sie nutzen. Sie erläutert auch,
                    wie und zu welchem Zweck das geschieht.
                  </p>
                  <p>
                    Wir weisen darauf hin, dass die Datenübertragung im Internet
                    (z. B. bei der Kommunikation per E-Mail) Sicherheitslücken
                    aufweisen kann. Ein lückenloser Schutz der Daten vor dem
                    Zugriff durch Dritte ist nicht möglich.
                  </p>
                </div>
                <div className={blockSpacing}>
                  <SubHeading id="verantwortliche-stelle-2">
                    Hinweis zur verantwortlichen Stelle
                  </SubHeading>
                  <p>
                    Die verantwortliche Stelle für die Datenverarbeitung auf
                    dieser Website ist:
                  </p>
                  <p className="font-medium">
                    Autohaus J. Wiest &amp; Söhne GmbH
                  </p>
                  <p>Geschäftsführung: Moritz Nitsche</p>
                  <p>Hilpertstraße 6</p>
                  <p>64295 Darmstadt</p>
                  <p>Telefon: (06151) 864-0</p>
                  <p>E-Mail: info@wiest-autohaeuser.de</p>
                  <p>
                    Verantwortliche Stelle ist die natürliche oder juristische
                    Person, die allein oder gemeinsam mit anderen über die
                    Zwecke und Mittel der Verarbeitung von personenbezogenen
                    Daten (z. B. Namen, E-Mail-Adressen o. Ä.) entscheidet.
                  </p>
                </div>
                <div className={blockSpacing}>
                  <SubHeading id="speicherdauer">Speicherdauer</SubHeading>
                  <p>
                    Soweit innerhalb dieser Datenschutzerklärung keine
                    speziellere Speicherdauer genannt wurde, verbleiben Ihre
                    personenbezogenen Daten bei uns, bis der Zweck für die
                    Datenverarbeitung entfällt. Wenn Sie ein berechtigtes
                    Löschersuchen geltend machen oder eine Einwilligung zur
                    Datenverarbeitung widerrufen, werden Ihre Daten gelöscht,
                    sofern wir keine anderen rechtlich zulässigen Gründe für die
                    Speicherung Ihrer personenbezogenen Daten haben (z. B.
                    steuer- oder handelsrechtliche Aufbewahrungsfristen); im
                    letztgenannten Fall erfolgt die Löschung nach Fortfall
                    dieser Gründe.
                  </p>
                </div>
                <div className={blockSpacing}>
                  <SubHeading id="rechtsgrundlagen">
                    Allgemeine Hinweise zu den Rechtsgrundlagen der
                    Datenverarbeitung auf dieser Website
                  </SubHeading>
                  <p>
                    Sofern Sie in die Datenverarbeitung eingewilligt haben,
                    verarbeiten wir Ihre personenbezogenen Daten auf Grundlage
                    von Art. 6 Abs. 1 lit. a DSGVO bzw. Art. 9 Abs. 2 lit. a
                    DSGVO, sofern besondere Datenkategorien nach Art. 9 Abs. 1
                    DSGVO verarbeitet werden. Im Falle einer ausdrücklichen
                    Einwilligung in die Übertragung personenbezogener Daten in
                    Drittstaaten erfolgt die Datenverarbeitung außerdem auf
                    Grundlage von Art. 49 Abs. 1 lit. a DSGVO. Sofern Sie in die
                    Speicherung von Cookies oder in den Zugriff auf
                    Informationen in Ihr Endgerät (z. B. via
                    Device-Fingerprinting) eingewilligt haben, erfolgt die
                    Datenverarbeitung zusätzlich auf Grundlage von § 25 Abs. 1
                    TTDSG. Die Einwilligung ist jederzeit widerrufbar. Sind Ihre
                    Daten zur Vertragserfüllung oder zur Durchführung
                    vorvertraglicher Maßnahmen erforderlich, verarbeiten wir
                    Ihre Daten auf Grundlage des Art. 6 Abs. 1 lit. b DSGVO. Des
                    Weiteren verarbeiten wir Ihre Daten, sofern diese zur
                    Erfüllung einer rechtlichen Verpflichtung erforderlich sind
                    auf Grundlage von Art. 6 Abs. 1 lit. c DSGVO. Die
                    Datenverarbeitung kann ferner auf Grundlage unseres
                    berechtigten Interesses nach Art. 6 Abs. 1 lit. f DSGVO
                    erfolgen. Über die jeweils im Einzelfall einschlägigen
                    Rechtsgrundlagen wird in den folgenden Absätzen dieser
                    Datenschutzerklärung informiert.
                  </p>
                </div>
                <div className={blockSpacing}>
                  <SubHeading id="datenschutzbeauftragter">
                    Datenschutzbeauftragter
                  </SubHeading>
                  <p>Wir haben einen Datenschutzbeauftragten benannt.</p>
                  <p className="font-medium">entplexit GmbH</p>
                  <p>Datenschutzbeauftragter</p>
                  <p>Kölner Straße 12</p>
                  <p>65760 Eschborn.</p>
                  <p>E-Mail: datenschutz@wiest-autohaeuser.de</p>
                </div>
                <div className={blockSpacing}>
                  <SubHeading id="datenweitergabe">
                    Hinweis zur Datenweitergabe in die USA und sonstige
                    Drittstaaten
                  </SubHeading>
                  <p>
                    Wir verwenden unter anderem Tools von Unternehmen mit Sitz
                    in den USA oder sonstigen datenschutzrechtlich nicht
                    sicheren Drittstaaten. Wenn diese Tools aktiv sind, können
                    Ihre personenbezogene Daten in diese Drittstaaten übertragen
                    und dort verarbeitet werden. Wir weisen darauf hin, dass in
                    diesen Ländern kein mit der EU vergleichbares
                    Datenschutzniveau garantiert werden kann. Beispielsweise
                    sind US-Unternehmen dazu verpflichtet, personenbezogene
                    Daten an Sicherheitsbehörden herauszugeben, ohne dass Sie
                    als Betroffener hiergegen gerichtlich vorgehen könnten. Es
                    kann daher nicht ausgeschlossen werden, dass US-Behörden (z.
                    B. Geheimdienste) Ihre auf US-Servern befindlichen Daten zu
                    Überwachungszwecken verarbeiten, auswerten und dauerhaft
                    speichern. Wir haben auf diese Verarbeitungstätigkeiten
                    keinen Einfluss.
                  </p>
                </div>
                <div className={blockSpacing}>
                  <SubHeading id="widerruf">
                    Widerruf Ihrer Einwilligung zur Datenverarbeitung
                  </SubHeading>
                  <p>
                    Viele Datenverarbeitungsvorgänge sind nur mit Ihrer
                    ausdrücklichen Einwilligung möglich. Sie können eine bereits
                    erteilte Einwilligung jederzeit widerrufen. Die
                    Rechtmäßigkeit der bis zum Widerruf erfolgten
                    Datenverarbeitung bleibt vom Widerruf unberührt.
                  </p>
                </div>
                <div className={blockSpacing}>
                  <SubHeading id="widerspruch">
                    Widerspruchsrecht gegen die Datenerhebung in besonderen
                    Fällen sowie gegen Direktwerbung (Art. 21 DSGVO)
                  </SubHeading>
                  <p>
                    <strong>
                      WENN DIE DATENVERARBEITUNG AUF GRUNDLAGE VON ART. 6 ABS. 1
                      LIT. E ODER F DSGVO ERFOLGT, HABEN SIE JEDERZEIT DAS
                      RECHT, AUS GRÜNDEN, DIE SICH AUS IHRER BESONDEREN
                      SITUATION ERGEBEN, GEGEN DIE VERARBEITUNG IHRER
                      PERSONENBEZOGENEN DATEN WIDERSPRUCH EINZULEGEN; DIES GILT
                      AUCH FÜR EIN AUF DIESE BESTIMMUNGEN GESTÜTZTES PROFILING.
                      DIE JEWEILIGE RECHTSGRUNDLAGE, AUF DENEN EINE VERARBEITUNG
                      BERUHT, ENTNEHMEN SIE DIESER DATENSCHUTZERKLÄRUNG. WENN
                      SIE WIDERSPRUCH EINLEGEN, WERDEN WIR IHRE BETROFFENEN
                      PERSONENBEZOGENEN DATEN NICHT MEHR VERARBEITEN, ES SEI
                      DENN, WIR KÖNNEN ZWINGENDE SCHUTZWÜRDIGE GRÜNDE FÜR DIE
                      VERARBEITUNG NACHWEISEN, DIE IHRE INTERESSEN, RECHTE UND
                      FREIHEITEN ÜBERWIEGEN ODER DIE VERARBEITUNG DIENT DER
                      GELTENDMACHUNG, AUSÜBUNG ODER VERTEIDIGUNG VON
                      RECHTSANSPRÜCHEN (WIDERSPRUCH NACH ART. 21 ABS. 1 DSGVO).
                    </strong>
                  </p>
                  <p>
                    <strong>
                      WERDEN IHRE PERSONENBEZOGENEN DATEN VERARBEITET, UM
                      DIREKTWERBUNG ZU BETREIBEN, SO HABEN SIE DAS RECHT,
                      JEDERZEIT WIDERSPRUCH GEGEN DIE VERARBEITUNG SIE
                      BETREFFENDER PERSONENBEZOGENER DATEN ZUM ZWECKE DERARTIGER
                      WERBUNG EINZULEGEN; DIES GILT AUCH FÜR DAS PROFILING,
                      SOWEIT ES MIT SOLCHER DIREKTWERBUNG IN VERBINDUNG STEHT.
                      WENN SIE WIDERSPRECHEN, WERDEN IHRE PERSONENBEZOGENEN
                      DATEN ANSCHLIESSEND NICHT MEHR ZUM ZWECKE DER
                      DIREKTWERBUNG VERWENDET (WIDERSPRUCH NACH ART. 21 ABS. 2
                      DSGVO).
                    </strong>
                  </p>
                </div>
                <div className={blockSpacing}>
                  <SubHeading id="aufsichtsbehoerde">
                    Beschwerderecht bei der zuständigen Aufsichtsbehörde
                  </SubHeading>
                  <p>
                    Im Falle von Verstößen gegen die DSGVO steht den Betroffenen
                    ein Beschwerderecht bei einer Aufsichtsbehörde, insbesondere
                    in dem Mitgliedstaat ihres gewöhnlichen Aufenthalts, ihres
                    Arbeitsplatzes oder des Orts des mutmaßlichen Verstoßes zu.
                    Das Beschwerderecht besteht unbeschadet anderweitiger
                    verwaltungsrechtlicher oder gerichtlicher Rechtsbehelfe.
                  </p>
                </div>
                <div className={blockSpacing}>
                  <SubHeading id="datenuebertragbarkeit">
                    Recht auf Datenübertragbarkeit
                  </SubHeading>
                  <p>
                    Sie haben das Recht, Daten, die wir auf Grundlage Ihrer
                    Einwilligung oder in Erfüllung eines Vertrags automatisiert
                    verarbeiten, an sich oder an einen Dritten in einem
                    gängigen, maschinenlesbaren Format aushändigen zu lassen.
                    Sofern Sie die direkte Übertragung der Daten an einen
                    anderen Verantwortlichen verlangen, erfolgt dies nur, soweit
                    es technisch machbar ist.
                  </p>
                </div>
                <div className={blockSpacing}>
                  <SubHeading id="auskunft-loeschung">
                    Auskunft, Löschung und Berichtigung
                  </SubHeading>
                  <p>
                    Sie haben im Rahmen der geltenden gesetzlichen Bestimmungen
                    jederzeit das Recht auf unentgeltliche Auskunft über Ihre
                    gespeicherten personenbezogenen Daten, deren Herkunft und
                    Empfänger und den Zweck der Datenverarbeitung und ggf. ein
                    Recht auf Berichtigung oder Löschung dieser Daten. Hierzu
                    sowie zu weiteren Fragen zum Thema personenbezogene Daten
                    können Sie sich jederzeit an uns wenden.
                  </p>
                </div>
                <div className={blockSpacing}>
                  <SubHeading id="einschraenkung">
                    Recht auf Einschränkung der Verarbeitung
                  </SubHeading>
                  <p>
                    Sie haben das Recht, die Einschränkung der Verarbeitung
                    Ihrer personenbezogenen Daten zu verlangen. Hierzu können
                    Sie sich jederzeit an uns wenden. Das Recht auf
                    Einschränkung der Verarbeitung besteht in folgenden Fällen:
                  </p>
                  <ul className="ml-6 list-disc space-y-2">
                    <li>
                      Wenn Sie die Richtigkeit Ihrer bei uns gespeicherten
                      personenbezogenen Daten bestreiten, benötigen wir in der
                      Regel Zeit, um dies zu überprüfen. Für die Dauer der
                      Prüfung haben Sie das Recht, die Einschränkung der
                      Verarbeitung Ihrer personenbezogenen Daten zu verlangen.
                    </li>
                    <li>
                      Wenn die Verarbeitung Ihrer personenbezogenen Daten
                      unrechtmäßig geschah/geschieht, können Sie statt der
                      Löschung die Einschränkung der Datenverarbeitung
                      verlangen.
                    </li>
                    <li>
                      Wenn wir Ihre personenbezogenen Daten nicht mehr
                      benötigen, Sie sie jedoch zur Ausübung, Verteidigung oder
                      Geltendmachung von Rechtsansprüchen benötigen, haben Sie
                      das Recht, statt der Löschung die Einschränkung der
                      Verarbeitung Ihrer personenbezogenen Daten zu verlangen.
                    </li>
                    <li>
                      Wenn Sie einen Widerspruch nach Art. 21 Abs. 1 DSGVO
                      eingelegt haben, muss eine Abwägung zwischen Ihren und
                      unseren Interessen vorgenommen werden. Solange noch nicht
                      feststeht, wessen Interessen überwiegen, haben Sie das
                      Recht, die Einschränkung der Verarbeitung Ihrer
                      personenbezogenen Daten zu verlangen.
                    </li>
                  </ul>
                  <p>
                    Wenn Sie die Verarbeitung Ihrer personenbezogenen Daten
                    eingeschränkt haben, dürfen diese Daten – von ihrer
                    Speicherung abgesehen – nur mit Ihrer Einwilligung oder zur
                    Geltendmachung, Ausübung oder Verteidigung von
                    Rechtsansprüchen oder zum Schutz der Rechte einer anderen
                    natürlichen oder juristischen Person oder aus Gründen eines
                    wichtigen öffentlichen Interesses der Europäischen Union
                    oder eines Mitgliedstaats verarbeitet werden.
                  </p>
                </div>
                <div className={blockSpacing}>
                  <SubHeading id="ssl-tls">
                    SSL- bzw. TLS-Verschlüsselung
                  </SubHeading>
                  <p>
                    Diese Seite nutzt aus Sicherheitsgründen und zum Schutz der
                    Übertragung vertraulicher Inhalte, wie zum Beispiel
                    Bestellungen oder Anfragen, die Sie an uns als
                    Seitenbetreiber senden, eine SSL- bzw. TLS-Verschlüsselung.
                    Eine verschlüsselte Verbindung erkennen Sie daran, dass die
                    Adresszeile des Browsers von „http://“ auf „https://“
                    wechselt und an dem Schloss-Symbol in Ihrer Browserzeile.
                  </p>
                  <p>
                    Wenn die SSL- bzw. TLS-Verschlüsselung aktiviert ist, können
                    die Daten, die Sie an uns übermitteln, nicht von Dritten
                    mitgelesen werden.
                  </p>
                </div>
                <div className={blockSpacing}>
                  <SubHeading id="werbe-emails">
                    Widerspruch gegen Werbe-E-Mails
                  </SubHeading>
                  <p>
                    Der Nutzung von im Rahmen der Impressumspflicht
                    veröffentlichten Kontaktdaten zur Übersendung von nicht
                    ausdrücklich angeforderter Werbung und
                    Informationsmaterialien wird hiermit widersprochen. Die
                    Betreiber der Seiten behalten sich ausdrücklich rechtliche
                    Schritte im Falle der unverlangten Zusendung von
                    Werbeinformationen, etwa durch Spam-E-Mails, vor.
                  </p>
                </div>
              </div>
            </section>

            {/* 5. Datenerfassung auf dieser Website (Server-Log, Kontakt, Cookies) */}
            <section className="mb-8 scroll-mt-24" id="datenerfassung-website">
              <h2 className="heading-medium mb-4 scroll-mt-24">
                <span className={getHeadingHighlight("datenerfassung-website")}>
                  5. Datenerfassung auf dieser Website
                </span>
              </h2>
              <h3
                className="heading-small mt-4 mb-1 scroll-mt-24"
                id="server-log"
              >
                <span className={`inline ${getHeadingHighlight("server-log")}`}>
                  Server-Log-Dateien
                </span>
              </h3>
              <p>
                Der Provider der Seiten erhebt und speichert automatisch
                Informationen in so genannten Server-Log-Dateien, die Ihr
                Browser automatisch an uns übermittelt. Dies sind:
              </p>
              <ul className="mb-4 ml-6 list-disc">
                <li>Browsertyp und Browserversion</li>
                <li>verwendetes Betriebssystem</li>
                <li>Referrer URL</li>
                <li>Hostname des zugreifenden Rechners</li>
                <li>Uhrzeit der Serveranfrage</li>
                <li>IP-Adresse</li>
              </ul>
              <p>
                Eine Zusammenführung dieser Daten mit anderen Datenquellen wird
                nicht vorgenommen.
              </p>
              <p>
                Die Erfassung dieser Daten erfolgt auf Grundlage von Art. 6 Abs.
                1 lit. f DSGVO. Der Websitebetreiber hat ein berechtigtes
                Interesse an der technisch fehlerfreien Darstellung und der
                Optimierung seiner Website – hierzu müssen die Server-Log-Files
                erfasst werden.
              </p>
              <h3
                className="heading-small mt-4 mb-1 scroll-mt-24"
                id="kontaktformular"
              >
                <span
                  className={`inline ${getHeadingHighlight("kontaktformular")}`}
                >
                  Kontaktformular
                </span>
              </h3>
              <p>
                Wenn Sie uns per Kontaktformular Anfragen zukommen lassen,
                werden Ihre Angaben aus dem Anfrageformular inklusive der von
                Ihnen dort angegebenen Kontaktdaten zwecks Bearbeitung der
                Anfrage und für den Fall von Anschlussfragen bei uns
                gespeichert. Diese Daten geben wir nicht ohne Ihre Einwilligung
                weiter.
              </p>
              <p>
                Die Verarbeitung dieser Daten erfolgt auf Grundlage von Art. 6
                Abs. 1 lit. b DSGVO, sofern Ihre Anfrage mit der Erfüllung eines
                Vertrags zusammenhängt oder zur Durchführung vorvertraglicher
                Maßnahmen erforderlich ist. In allen übrigen Fällen beruht die
                Verarbeitung auf unserem berechtigten Interesse an der
                effektiven Bearbeitung der an uns gerichteten Anfragen (Art. 6
                Abs. 1 lit. f DSGVO) oder auf Ihrer Einwilligung (Art. 6 Abs. 1
                lit. a DSGVO) sofern diese abgefragt wurde; die Einwilligung ist
                jederzeit widerrufbar.
              </p>
              <p>
                Die von Ihnen im Kontaktformular eingegebenen Daten verbleiben
                bei uns, bis Sie uns zur Löschung auffordern, Ihre Einwilligung
                zur Speicherung widerrufen oder der Zweck für die
                Datenspeicherung entfällt (z. B. nach abgeschlossener
                Bearbeitung Ihrer Anfrage). Zwingende gesetzliche Bestimmungen –
                insbesondere Aufbewahrungsfristen – bleiben unberührt.
              </p>
              <h3 className="heading-small mt-4 mb-1 scroll-mt-24" id="anfrage">
                <span className={`inline ${getHeadingHighlight("anfrage")}`}>
                  Anfrage per E-Mail, Telefon oder Telefax
                </span>
              </h3>
              <p>
                Wenn Sie uns per E-Mail, Telefon oder Telefax kontaktieren, wird
                Ihre Anfrage inklusive aller daraus hervorgehenden
                personenbezogenen Daten (Name, Anfrage) zum Zwecke der
                Bearbeitung Ihres Anliegens bei uns gespeichert und verarbeitet.
                Diese Daten geben wir nicht ohne Ihre Einwilligung weiter.
              </p>
              <p>
                Die Verarbeitung dieser Daten erfolgt auf Grundlage von Art. 6
                Abs. 1 lit. b DSGVO, sofern Ihre Anfrage mit der Erfüllung eines
                Vertrags zusammenhängt oder zur Durchführung vorvertraglicher
                Maßnahmen erforderlich ist. In allen übrigen Fällen beruht die
                Verarbeitung auf unserem berechtigten Interesse an der
                effektiven Bearbeitung der an uns gerichteten Anfragen (Art. 6
                Abs. 1 lit. f DSGVO) oder auf Ihrer Einwilligung (Art. 6 Abs. 1
                lit. a DSGVO) sofern diese abgefragt wurde; die Einwilligung ist
                jederzeit widerrufbar.
              </p>
              <p>
                Die von Ihnen an uns per Kontaktanfragen übersandten Daten
                verbleiben bei uns, bis Sie uns zur Löschung auffordern, Ihre
                Einwilligung zur Speicherung widerrufen oder der Zweck für die
                Datenspeicherung entfällt (z. B. nach abgeschlossener
                Bearbeitung Ihres Anliegens). Zwingende gesetzliche Bestimmungen
                – insbesondere gesetzliche Aufbewahrungsfristen – bleiben
                unberührt.
              </p>
              {isSchuetzTenant ? (
                <div className={blockSpacing}>
                  <h3
                    className="heading-small mt-4 mb-1 scroll-mt-24"
                    id="ki-sprachassistent"
                  >
                    <span className={getHeadingHighlight("ki-sprachassistent")}>
                      Einsatz eines KI-gestützten Sprachassistenten zur
                      Entgegennahme eingehender Anrufe
                    </span>
                  </h3>
                  <div className={denseBlockSpacing}>
                    <p>
                      Zur automatisierten Bearbeitung telefonischer Anfragen
                      setzen wir den STRATO Smart-Telefonassistenten ein. Dieser
                      verarbeitet Gesprächsinhalte in unserem Auftrag, um Ihr
                      Anliegen zu erfassen, weiterzuleiten oder zu beantworten.
                      Dabei kommen automatische Spracherkennung
                      (Speech-to-Text), semantische Analyse (Natural Language
                      Processing) und ggf. Textgenerierung (Text-to-Speech) zum
                      Einsatz.
                    </p>
                    <p>
                      Gesprächsinhalte können aufgezeichnet, transkribiert oder
                      dokumentiert werden. Dies erfolgt nur, wenn Sie zu Beginn
                      des Gesprächs entsprechend informiert wurden und das
                      Gespräch fortsetzen. Sie können die Verbindung jederzeit
                      beenden, um eine Verarbeitung zu vermeiden.
                    </p>
                    <p>
                      Verarbeitet werden insbesondere Ihre Äußerungen im
                      Gespräch, technische Verbindungsdaten (Datum, Uhrzeit,
                      Dauer) sowie ggf. freiwillig mitgeteilte Stammdaten wie
                      Name oder Telefonnummer. Zudem werden Interaktionsdaten
                      verarbeitet, etwa zur Gesprächsführung oder bei Abbrüchen.
                    </p>
                    <p>
                      Rechtsgrundlagen sind Art. 6 Abs. 1 lit. b DSGVO (zur
                      Vertragsanbahnung oder -durchführung) und Art. 6 Abs. 1
                      lit. f DSGVO (berechtigtes Interesse an effizienter und
                      skalierbarer Kommunikation).
                    </p>
                    <p>
                      Zur technischen Umsetzung setzen wir Subdienstleister ein,
                      insbesondere für Hosting, Spracherkennung und semantische
                      Analyse. Dabei können personenbezogene Daten durch den
                      Subdienstleister auch in die USA übermittelt werden.
                      Grundsätzlich erfolgt dies auf Grundlage eines
                      Angemessenheitsbeschlusses nach Art. 45 DSGVO, da die
                      Dienstleister nach dem EU-U.S. Data Privacy Framework
                      zertifiziert sind. Fehlt ein solcher Beschluss, erfolgt
                      die Übermittlung auf Basis von Standardvertragsklauseln
                      gemäß Art. 46 Abs. 2 lit. c DSGVO und ergänzender
                      Schutzmaßnahmen. Die Vorgaben der Art. 44 DSGVO werden
                      eingehalten.
                    </p>
                    <p>
                      Zur Verbesserung der Sprachverarbeitung können
                      pseudonymisierte Daten zu Trainingszwecken verwendet
                      werden. Eine Rückverfolgung auf einzelne Personen ist
                      dabei ausgeschlossen oder erheblich erschwert. Eine
                      automatisierte Entscheidungsfindung im Sinne des Art. 22
                      DSGVO findet nicht statt.
                    </p>
                    <p>
                      Die Speicherung erfolgt nur so lange, wie es für die
                      genannten Zwecke erforderlich ist. Anschließend werden die
                      Daten automatisch gelöscht oder anonymisiert.
                    </p>
                  </div>
                </div>
              ) : null}
              <h3 className="heading-small mt-4 mb-1 scroll-mt-24" id="cookies">
                <span className={`inline ${getHeadingHighlight("cookies")}`}>
                  Cookies und Einwilligungs-Management
                  (&ldquo;Cookie-Banner&rdquo;)
                </span>
              </h3>
              <p>
                Unsere Website verwendet Cookies und ähnliche Technologien (z.
                B. Scripts, Pixel, Local Storage), um bestimmte Funktionen
                bereitzustellen, die Nutzung der Website zu analysieren und
                Online-Marketing-Maßnahmen umzusetzen.
              </p>
              <p>
                Cookies sind kleine Textdateien, die auf Ihrem Endgerät
                gespeichert werden und die Ihr Browser speichert. Einige Cookies
                sind notwendig, damit die Website technisch funktioniert
                (&ldquo;notwendige Cookies&rdquo;). Andere Cookies dienen der
                Statistik, der Reichweitenmessung oder Marketing-Zwecken (z. B.
                zur Anzeige interessenbasierter Werbung, Conversion-Messung).
              </p>
              <p>
                Wir setzen ein Einwilligungs-Management-Tool (Cookie-Banner)
                ein. Beim ersten Besuch unserer Website werden Sie gefragt, ob
                Sie in bestimmte Kategorien von Cookies und Tools einwilligen
                möchten (z. B. &ldquo;Statistik&rdquo;,
                &ldquo;Marketing&rdquo;). Erst nach Ihrer Einwilligung werden
                die entsprechenden Cookies gesetzt bzw. entsprechende Skripte
                geladen. Sie können Ihre Einwilligung jederzeit mit Wirkung für
                die Zukunft widerrufen oder Ihre Auswahl ändern, indem Sie die
                Cookie-Einstellungen über den entsprechenden Link oder das
                Symbol auf unserer Website erneut aufrufen.
              </p>
              <h4 className="heading-small mt-2 mb-1 scroll-mt-24">
                Rechtsgrundlage
              </h4>
              <p>
                Für unbedingt erforderliche Cookies: Art. 6 Abs. 1 lit. f DSGVO
                i. V. m. § 25 Abs. 2 TTDSG (berechtigtes Interesse an einer
                funktionsfähigen Website).
              </p>
              <p>
                Für alle anderen Cookies/Tools (z. B. Analyse, Marketing): Art.
                6 Abs. 1 lit. a DSGVO i. V. m. § 25 Abs. 1 TTDSG (Einwilligung).
              </p>
              <h3
                className="heading-small mt-4 mb-1 scroll-mt-24"
                id="google-tag-manager"
              >
                <span
                  className={`inline ${getHeadingHighlight("google-tag-manager")}`}
                >
                  Google Tag Manager
                </span>
              </h3>
              <p>
                Wir setzen den Google Tag Manager ein. Anbieter ist die Google
                Ireland Limited (&ldquo;Google&rdquo;), Gordon House, Barrow
                Street, Dublin 4, Irland. Der Google Tag Manager ist ein
                Werkzeug, mit dem wir Tracking- und Analyse-Tools (z. B. Google
                Analytics) auf unserer Website verwalten und einbinden. Der Tag
                Manager selbst erstellt keine Nutzerprofile, speichert keine
                Cookies und nimmt keine eigenständigen Analysen vor. Er dient
                lediglich der Verwaltung und Ausspielung der über ihn
                eingebundenen Tools. Der Google Tag Manager erfasst jedoch Ihre
                IP-Adresse, die auch an das Mutterunternehmen von Google in die
                USA übertragen werden kann.
              </p>
              <p>
                Der Google Tag Manager wird ausschließlich geladen, sofern Sie
                über unseren Cookie-Banner in die Kategorie
                &ldquo;Analyse&rdquo; eingewilligt haben. Rechtsgrundlage ist
                Ihre Einwilligung (Art. 6 Abs. 1 lit. a DSGVO, § 25 Abs. 1
                TTDSG). Sie können Ihre Einwilligung jederzeit über die
                Cookie-Einstellungen auf unserer Website widerrufen. Die über
                den Tag Manager eingebundenen Tools erhalten Ihre jeweilige
                Einwilligungsentscheidung je Kategorie (Consent Mode).
              </p>
              <h3
                className="heading-small mt-4 mb-1 scroll-mt-24"
                id="google-analytics"
              >
                <span
                  className={`inline ${getHeadingHighlight("google-analytics")}`}
                >
                  Google Analytics
                </span>
              </h3>
              <p>
                Wir setzen auf unserer Website den Webanalysedienst Google
                Analytics ein, sofern Sie dem über unseren Cookie-Banner
                zugestimmt haben. Anbieter ist die Google Ireland Limited
                (&ldquo;Google&rdquo;), Gordon House, Barrow Street, Dublin 4,
                Irland.
              </p>
              <p>
                Google Analytics verwendet Cookies und ähnliche Technologien,
                die eine Analyse der Benutzung der Website durch Sie
                ermöglichen. Die durch das Cookie erzeugten Informationen über
                Ihre Benutzung dieser Website (u. a. IP-Adresse, Browser-Typ,
                Betriebssystem, aufgerufene Seiten, Verweildauer,
                Herkunftsquelle) werden in der Regel an einen Server von Google
                übertragen und dort gespeichert. Dabei kann es zu einer
                Übermittlung in die USA kommen.
              </p>
              <p>
                Wir setzen Google Analytics mit IP-Anonymisierung ein. Das
                bedeutet, Ihre IP-Adresse wird von Google innerhalb der EU oder
                des EWR gekürzt, bevor sie in die USA übertragen wird. Nur in
                Ausnahmefällen wird die volle IP-Adresse an einen Server von
                Google in den USA übertragen und dort gekürzt. Laut Google wird
                die im Rahmen von Google Analytics von Ihrem Browser
                übermittelte IP-Adresse nicht mit anderen Daten von Google
                zusammengeführt.
              </p>
              <p>
                Google nutzt die erhobenen Daten in unserem Auftrag, um die
                Nutzung der Website auszuwerten, Reports über die
                Websiteaktivitäten zusammenzustellen und weitere mit der
                Websitenutzung und der Internetnutzung verbundene
                Dienstleistungen gegenüber uns zu erbringen.
              </p>
              <h4 className="heading-small mt-2 mb-1 scroll-mt-24">
                Rechtsgrundlage
              </h4>
              <p>
                Die Nutzung von Google Analytics erfolgt ausschließlich auf
                Grundlage Ihrer Einwilligung (Art. 6 Abs. 1 lit. a DSGVO, § 25
                Abs. 1 TTDSG). Sie können Ihre Einwilligung jederzeit über die
                Cookie-Einstellungen auf unserer Website widerrufen.
              </p>
              <h4 className="heading-small mt-2 mb-1 scroll-mt-24">
                Datenübermittlung in Drittstaaten
              </h4>
              <p>
                Eine Übermittlung personenbezogener Daten in die USA kann nicht
                ausgeschlossen werden. Die Datenübertragung stützt sich auf die
                von Google verwendeten EU-Standardvertragsklauseln. Nähere
                Informationen erhalten Sie in den Datenschutzhinweisen von
                Google.
              </p>
              <p>
                Mit Google haben wir einen Vertrag über Auftragsverarbeitung
                (AVV) abgeschlossen.
              </p>
              <h3
                className="heading-small mt-4 mb-1 scroll-mt-24"
                id="meta-pixel"
              >
                <span className={`inline ${getHeadingHighlight("meta-pixel")}`}>
                  Meta Pixel (Facebook-Pixel) & Facebook/Instagram
                  Conversion-Tracking
                </span>
              </h3>
              <p>
                Wir nutzen auf unserer Website – sofern Sie eingewilligt haben –
                das Meta Pixel (häufig als &ldquo;Facebook-Pixel&rdquo;
                bezeichnet) des Anbieters Meta Platforms Ireland Limited, 4
                Grand Canal Square, Grand Canal Harbour, Dublin 2, Irland
                (&ldquo;Meta&rdquo;).
              </p>
              <p>
                Mit Hilfe des Meta Pixels kann das Verhalten von Besuchern
                nachverfolgt werden, nachdem diese durch Klick auf eine
                Facebook- oder Instagram-Werbeanzeige auf unsere Website
                weitergeleitet wurden. Dadurch können wir die Wirksamkeit
                unserer Werbeanzeigen für statistische und Marktforschungszwecke
                auswerten und unsere Werbemaßnahmen optimieren
                (Conversion-Messung). Außerdem können wir sogenannte Custom
                Audiences bilden, um Besuchern unserer Website auf Facebook und
                Instagram interessenbezogene Werbung anzeigen zu lassen.
              </p>
              <p>
                Hierzu wird über das Meta Pixel eine direkte Verbindung zu den
                Meta-Servern hergestellt, wenn Sie unsere Website besuchen.
                Dabei kann Meta erkennen, dass Sie unsere Website besucht haben
                und – sofern Sie bei einem Meta-Dienst eingeloggt sind – diesen
                Besuch Ihrem Nutzerkonto zuordnen. Die von Meta erhobenen Daten
                sind für uns als Betreiber anonym; wir können keine Rückschlüsse
                auf die Identität der Nutzer ziehen. Die Daten werden jedoch von
                Meta zu eigenen Zwecken verarbeitet und mit weiteren Daten Ihres
                Meta-Profils verknüpft. Auf diese Verarbeitung haben wir keinen
                Einfluss.
              </p>
              <h4 className="heading-small mt-2 mb-1 scroll-mt-24">
                Rechtsgrundlage
              </h4>
              <p>
                Die Nutzung des Meta Pixels und damit verbundener Funktionen (z.
                B. Custom Audiences, Conversion-Tracking) erfolgt ausschließlich
                auf Grundlage Ihrer Einwilligung (Art. 6 Abs. 1 lit. a DSGVO, §
                25 Abs. 1 TTDSG). Die Einwilligung wird über unseren
                Cookie-Banner eingeholt und kann dort jederzeit mit Wirkung für
                die Zukunft widerrufen werden.
              </p>
              <h4 className="heading-small mt-2 mb-1 scroll-mt-24">
                Datenübermittlung in Drittstaaten
              </h4>
              <p>
                Die Verarbeitung der Daten kann auch auf Servern in den USA
                erfolgen. Meta stützt sich hierbei auf die
                EU-Standardvertragsklauseln. Weitere Informationen zur
                Datenverarbeitung durch Meta können Sie den Datenschutzhinweisen
                von Meta entnehmen.
              </p>
              <p>
                Mit Meta haben wir – soweit erforderlich – eine Vereinbarung
                über die gemeinsame Verantwortlichkeit gemäß Art. 26 DSGVO
                (insbesondere hinsichtlich der Event-Daten) geschlossen. Meta
                ist insbesondere dafür verantwortlich, die Rechte betroffener
                Personen im Zusammenhang mit den durch Meta verarbeiteten Daten
                zu erfüllen.
              </p>
            </section>

            {/* 6. Newsletter */}
            <section className="mb-8 scroll-mt-24" id="newsletter">
              <h2 className="heading-medium mb-4 scroll-mt-24">
                <span className={getHeadingHighlight("newsletter")}>
                  6. Newsletter
                </span>
              </h2>
              <h3
                className="heading-small mt-4 mb-1 scroll-mt-24"
                id="newsletterdaten"
              >
                <span
                  className={`inline ${getHeadingHighlight("newsletterdaten")}`}
                >
                  Newsletter­daten
                </span>
              </h3>
              <p>
                Wenn Sie den auf der Website angebotenen Newsletter beziehen
                möchten, benötigen wir von Ihnen eine E-Mail-Adresse sowie
                Informationen, welche uns die Überprüfung gestatten, dass Sie
                der Inhaber der angegebenen E-Mail-Adresse sind und mit dem
                Empfang des Newsletters einverstanden sind. Weitere Daten werden
                nicht bzw. nur auf freiwilliger Basis erhoben. Diese Daten
                verwenden wir ausschließlich für den Versand der angeforderten
                Informationen und geben diese nicht an Dritte weiter.
              </p>
              <p>
                Die Verarbeitung der in das Newsletteranmeldeformular
                eingegebenen Daten erfolgt ausschließlich auf Grundlage Ihrer
                Einwilligung (Art. 6 Abs. 1 lit. a DSGVO). Die erteilte
                Einwilligung zur Speicherung der Daten, der E-Mail-Adresse sowie
                deren Nutzung zum Versand des Newsletters können Sie jederzeit
                widerrufen, etwa über den „Austragen”-Link im Newsletter. Die
                Rechtmäßigkeit der bereits erfolgten Datenverarbeitungsvorgänge
                bleibt vom Widerruf unberührt.
              </p>
              <p>
                Die von Ihnen zum Zwecke des Newsletter-Bezugs bei uns
                hinterlegten Daten werden von uns bis zu Ihrer Austragung aus
                dem Newsletter bei uns bzw. dem Newsletterdiensteanbieter
                gespeichert und nach der Abbestellung des Newsletters oder nach
                Zweckfortfall aus der Newsletterverteilerliste gelöscht. Wir
                behalten uns vor, E-Mail-Adressen aus unserem
                Newsletterverteiler nach eigenem Ermessen im Rahmen unseres
                berechtigten Interesses nach Art. 6 Abs. 1 lit. f DSGVO zu
                löschen oder zu sperren.
              </p>
              <p>
                Daten, die zu anderen Zwecken bei uns gespeichert wurden,
                bleiben hiervon unberührt.
              </p>
              <p>
                Nach Ihrer Austragung aus der Newsletterverteilerliste wird Ihre
                E-Mail-Adresse bei uns bzw. dem Newsletterdiensteanbieter ggf.
                in einer Blacklist gespeichert, sofern dies zur Verhinderung
                künftiger Mailings erforderlich ist. Die Daten aus der Blacklist
                werden nur für diesen Zweck verwendet und nicht mit anderen
                Daten zusammengeführt. Dies dient sowohl Ihrem Interesse als
                auch unserem Interesse an der Einhaltung der gesetzlichen
                Vorgaben beim Versand von Newslettern (berechtigtes Interesse im
                Sinne des Art. 6 Abs. 1 lit. f DSGVO). Die Speicherung in der
                Blacklist ist zeitlich nicht befristet.
              </p>
              <p>
                <strong>
                  Sie können der Speicherung widersprechen, sofern Ihre
                  Interessen unser berechtigtes Interesse überwiegen.
                </strong>
              </p>
            </section>

            {/* 7. Einbindung von externen Inhalten */}
            <section className="mb-8 scroll-mt-24" id="einbindung-externe">
              <h2 className="heading-medium mb-4 scroll-mt-24">
                <span className={getHeadingHighlight("einbindung-externe")}>
                  7. Einbindung von externen Inhalten
                </span>
              </h2>
              <h3 className="heading-small mt-4 mb-1 scroll-mt-24" id="youtube">
                <span className={`inline ${getHeadingHighlight("youtube")}`}>
                  Einbindung von YouTube
                </span>
              </h3>
              <p>
                Auf unserer Website binden wir Videos der Plattform YouTube ein,
                sofern Sie der Nutzung externer Medien über unseren
                Cookie-Banner zugestimmt haben. Anbieter ist Google Ireland
                Limited (&ldquo;Google&rdquo;), Gordon House, Barrow Street,
                Dublin 4, Irland.
              </p>
              <p>
                Wir verwenden in der Regel den von YouTube angebotenen
                &ldquo;erweiterten Datenschutzmodus&rdquo;. Laut YouTube werden
                in diesem Modus zunächst keine Informationen über Besucher an
                YouTube übertragen. Erst wenn Sie ein Video aktiv abspielen,
                werden Daten an YouTube/Google übermittelt. Auf diese
                Datenübertragung haben wir keinen Einfluss.
              </p>
              <p>Zu den dabei verarbeiteten Daten gehören insbesondere:</p>
              <ul className="mb-4 ml-6 list-disc">
                <li>IP-Adresse</li>
                <li>Geräte- und Browserinformationen</li>
                <li>aufgerufene Unterseite, Datum und Uhrzeit</li>
                <li>
                  ggf. Informationen aus Ihrem Google-/YouTube-Konto (wenn Sie
                  dort eingeloggt sind)
                </li>
              </ul>
              <p>
                Wenn Sie in Ihrem YouTube- oder Google-Konto eingeloggt sind,
                kann YouTube Ihr Surfverhalten direkt Ihrem persönlichen Profil
                zuordnen. Dies können Sie verhindern, indem Sie sich vor dem
                Abspielen von Videos aus Ihrem Konto ausloggen.
              </p>
              <h4 className="heading-small mt-2 mb-1 scroll-mt-24">
                Rechtsgrundlage
              </h4>
              <p>
                Die Einbindung von YouTube erfolgt ausschließlich auf Grundlage
                Ihrer Einwilligung (Art. 6 Abs. 1 lit. a DSGVO, § 25 Abs. 1
                TTDSG). Die Einwilligung können Sie jederzeit über die
                Cookie-Einstellungen widerrufen.
              </p>
              <h4 className="heading-small mt-2 mb-1 scroll-mt-24">
                Datenübermittlung in Drittstaaten
              </h4>
              <p>
                Es kann nicht ausgeschlossen werden, dass Daten auf Servern in
                den USA verarbeitet werden. Google stützt sich hierfür auf
                EU-Standardvertragsklauseln. Weitere Informationen finden Sie in
                den Datenschutzhinweisen von Google bzw. YouTube.
              </p>
              <h3
                className="heading-small mt-4 mb-1 scroll-mt-24"
                id="facebook-instagram"
              >
                <span
                  className={`inline ${getHeadingHighlight("facebook-instagram")}`}
                >
                  Einbindung von Facebook- und Instagram-Inhalten
                  (Social-Media-Plugins / Embeds)
                </span>
              </h3>
              <p>
                Auf unserer Website können Inhalte und Funktionen der sozialen
                Netzwerke Facebook und Instagram eingebunden sein, z. B.:
              </p>
              <ul className="mb-4 ml-6 list-disc">
                <li>Feeds (Timeline/Posts)</li>
                <li>
                  Like-Buttons, Teilen-Buttons oder ähnliche
                  Interaktions-Elemente
                </li>
                <li>eingebettete Beiträge oder Bilder</li>
              </ul>
              <p>
                Anbieter ist in beiden Fällen Meta Platforms Ireland Limited, 4
                Grand Canal Square, Grand Canal Harbour, Dublin 2, Irland
                (&ldquo;Meta&rdquo;).
              </p>
              <p>
                Wenn Sie eine Seite aufrufen, die ein solches
                Social-Media-Element enthält, wird zunächst – je nach
                Konfiguration – nur ein Platzhalter geladen. Erst wenn Sie über
                unseren Cookie-Banner oder durch aktives Anklicken zustimmen (z.
                B. &ldquo;Inhalte laden&rdquo;), wird eine direkte Verbindung
                zwischen Ihrem Browser und den Servern von Meta aufgebaut. Dabei
                werden Daten an Meta übertragen. Dazu können u. a. gehören:
              </p>
              <ul className="mb-4 ml-6 list-disc">
                <li>IP-Adresse</li>
                <li>Datum und Uhrzeit der Anfrage</li>
                <li>aufgerufene Seite/URL</li>
                <li>Browser-Informationen</li>
                <li>
                  ggf. Ihre Meta-ID, wenn Sie bei Facebook oder Instagram
                  eingeloggt sind
                </li>
              </ul>
              <p>
                Meta kann Ihren Besuch unserer Website Ihrem Benutzerkonto
                zuordnen, wenn Sie dort eingeloggt sind. Wenn Sie dies nicht
                wünschen, loggen Sie sich bitte vor dem Besuch unserer Website
                aus Ihren Facebook-/Instagram-Konten aus.
              </p>
              <h4 className="heading-small mt-2 mb-1 scroll-mt-24">
                Rechtsgrundlage
              </h4>
              <p>
                Die Darstellung von Facebook- und Instagram-Inhalten erfolgt
                ausschließlich auf Grundlage Ihrer Einwilligung (Art. 6 Abs. 1
                lit. a DSGVO, § 25 Abs. 1 TTDSG). Sie können Ihre Einwilligung
                jederzeit über die Cookie-Einstellungen widerrufen.
              </p>
              <h4 className="heading-small mt-2 mb-1 scroll-mt-24">
                Datenübermittlung in Drittstaaten
              </h4>
              <p>
                Die Daten können auf Servern in den USA verarbeitet werden. Meta
                stützt sich hierfür auf EU-Standardvertragsklauseln. Details
                entnehmen Sie bitte den Datenschutzhinweisen von Meta.
              </p>
            </section>

            {/* 8. Social-Media-Profile der WIEST GROUP */}
            <section className="mb-8 scroll-mt-24" id="social-media-profile">
              <h2 className="heading-medium mb-4 scroll-mt-24">
                <span className={getHeadingHighlight("social-media-profile")}>
                  8. Social-Media-Profile der WIEST GROUP (Hinweis)
                </span>
              </h2>
              <p>
                Die WIEST GROUP unterhält Unternehmensprofile in verschiedenen
                sozialen Netzwerken (z. B. Facebook, Instagram, YouTube, TikTok,
                LinkedIn), um dort mit Kunden und Interessenten zu kommunizieren
                und über Angebote rund um die Marken Volkswagen, Audi, Škoda und
                Porsche zu informieren.
              </p>
              <p>
                Bei dem Besuch dieser Unternehmensseiten gelten die Nutzungs-
                und Datenschutzbestimmungen der jeweiligen Plattformbetreiber.
                Die Datenverarbeitung erfolgt in gemeinsamer Verantwortung
                zwischen uns und den Plattformbetreibern, soweit wir auf die
                Verarbeitung Einfluss nehmen können (z. B. Auswertung von
                Statistiken).
              </p>
              <p>
                Informationen zur Datenverarbeitung durch uns im Rahmen der
                jeweiligen Social-Media-Profile sowie zu Ihren Rechten erhalten
                Sie auf den entsprechenden Profilen oder auf Anfrage bei uns.
              </p>
            </section>
          </div>
        </main>
      </Container>
    </>
  );
}
